import React, { memo, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";

import styles from "./formLogin.module.scss";
import { googleSVG, omuniSVG } from "../../../../../shared/helpers/icons";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import TextFieldMui from "../../../../../shared/components/TextFieldMui/TextFieldMui";
import { VIEWS } from "../../../../constants";
import { v4 as uuidv4 } from "uuid";
import { api, googleClientId } from "../../../../../app/conf";
import ButtonWithVariant from "../../../../../shared/components/ButtonWithVariant/ButtonWithVariant";
import { ButtonVariant } from "../../../../../shared/components/ButtonWithVariant/types";
import { recaptchaSiteKey } from "../../../../../app/conf";
import useRecaptcha from "../../../../hooks/useRecaptcha";
import SimpleCaptcha from "../SimpleCaptcha/SimpleCaptcha";
import { showGlobalError } from "../../../../../app/actions/app";
import { useDispatch } from "react-redux";

const POSITION_ICON = "end";

const AUTH_ERROR = "auth";

const AUTH_ERROR_MESSAGE = "No existe tu usuario en Omuni, contáctate con el administrador para solucionarlo.";

const EMPTY_STRING = "";

const TYPE_FIELD = {
  TEXT: "text",
  PASSWORD: "password",
};

const GOOGLE = "google";

const GOOGLE_SIGN_IN_URL = "https://accounts.google.com/gsi/client";

const GOOGLE_SCRIPT = "google-script";

const SCRIPT = "script";

const STATUS_DONE = "done";

const ENTER_KEY = "Enter";

const DEFAULT_LOGO = "/assets/icons/omuni_logo.svg";

const FormLogin = (loginProps) => {
  const {
    companyStore,
    signInStore,
    loginStore,
    postLoginRequest, 
    setRecoverPassword,
    setView,
    cleanLogin,
    signInRequest,
    history,
  } = loginProps;

  const dispatch = useDispatch();

  const { t: translations } = useTranslation([
    "login",
    "notification",
    "people",
  ]);

  const handleCaptcha = (result) => {
    setCaptchaVerified(result);
  };

  const validationSchema = Yup.object({
    username: Yup.string()
      .max(50)
      .required(translations("notification:enter_email")),
    password: Yup.string()
      .max(50)
      .required(translations("notification:enter_password")),
  });

  const recaptcha = useRecaptcha(recaptchaSiteKey);

  const [captchaVerified, setCaptchaVerified] = useState(false);

  const [success, setSuccess] = useState(false);

  const submitForm = async (_values) => {
    await recaptcha.generateRecaptchaToken();
    setSuccess(true);
  };

  const formik = useFormik({
    initialValues: {
      username: null,
      password: null,
    },
    validationSchema: validationSchema,
    onSubmit: submitForm,
  });

  useEffect(() => {
    if (success && recaptcha.token) {
      signInRequest(
        formik.values.username,
        formik.values.password,
        history,
        recaptcha
      );

      setSuccess(false);
    }
  }, [success]);

  const [showPassword, setShowPassword] = useState(false);

  const [uuid, setUuid] = useState(null);

  const [googleButtonLoaded, setGoogleButtonLoaded] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((oldValue) => setShowPassword(!oldValue));
  };

  const InputPasswordType = showPassword
    ? TYPE_FIELD.TEXT
    : TYPE_FIELD.PASSWORD;

  const ShowVisibilityIcon = () => {
    const Icon = showPassword ? <Visibility /> : <VisibilityOff />;

    return (
      <InputAdornment position={POSITION_ICON}>
        <IconButton
          aria-label="Toggle password visibility"
          onClick={handleClickShowPassword}
        >
          {Icon}
        </IconButton>
      </InputAdornment>
    );
  };

  const logo = useMemo(
    () => companyStore?.company?.picture?.logo || DEFAULT_LOGO,
    [companyStore]
  );

  const [isLogoLoaded, setIsLogoLoaded] = useState(false);

  const logoURL = useMemo(() => {
    return companyStore?.company?.picture?.logo || DEFAULT_LOGO;
  }, [companyStore]);

  useEffect(() => {
    const img = new Image();
    img.src = logoURL;
    img.onload = () => setIsLogoLoaded(true);
  }, [logoURL]);

  const logoStyle = isLogoLoaded
    ? { "--logo": isLogoLoaded ? `url(${logoURL})` : `url(${DEFAULT_LOGO})` }
    : { "--logo": `` };

  const containerClass = useMemo(() => {
    return isLogoLoaded
      ? `${styles.logoWrapper} ${styles.logoLoaded}`
      : styles.logoWrapper;
  }, [isLogoLoaded]);

  const handleGoogleLogin = () => {
    const URL = `${window.location.origin}/old/redirect_login/${uuid}`;

    postLoginRequest(GOOGLE, uuid, URL);
  };

  const handleOpenIdLogin = (provider) => {
    const URL = `${window.location.origin}/old/redirect_login/${uuid}`;
    const errorRedirectUrl = `${window.location.origin}/old/signin?error=auth`;
    postLoginRequest(provider, uuid, URL, errorRedirectUrl);
  };

  const handleGoogleButtonLoaded = () => {
    setGoogleButtonLoaded(true);
  };

  const handleVerifyResourceGoogle = () => {
    const collection = document.body.getElementsByClassName(GOOGLE_SCRIPT);

    if (collection.length > 0) {
      document.body.removeChild(collection[0]);
      window.location.reload(false);
    }
  };

  useEffect(() => {
    if (loginStore?.login && loginStore?.login?.provider === GOOGLE) {
      const script = document.createElement(SCRIPT);

      script.src = GOOGLE_SIGN_IN_URL;
      script.className = GOOGLE_SCRIPT;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      script.onload = handleGoogleButtonLoaded();
    }
  }, [loginStore?.login]);

  const handleKeyPress = (event) => {
    if (event.key !== ENTER_KEY) return;
    submitForm();
  };

  useEffect(() => {
    setUuid(uuidv4());
    cleanLogin();
    handleVerifyResourceGoogle();
    recaptcha.loadGoogleRecaptcha();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(searchParams.entries());
    if (params.error === AUTH_ERROR) {
      const errorMessage = params.error_message && params.error_message !== EMPTY_STRING 
        ? params.error_message 
        : AUTH_ERROR_MESSAGE;
      
      dispatch(showGlobalError(true, 405, errorMessage));
    }
  }, []);

  const isEcuadorCompany =
    companyStore.company && companyStore.company?.name === "stctea";

  const BUTTON_DISABLED =
    isEcuadorCompany &&
    !captchaVerified &&
    (formik.values.password !== "" || formik.values.username !== "");

  const IDPLogin = useMemo(() => {
    return companyStore?.company?.loginProviders;
  }, [companyStore]);

  return (
    <form className={styles.loginWrapper}>
      <div className={containerClass} style={logoStyle} />

      <div className={styles.contentWrapper}>
        <div className={styles.inputWrapper}>
          <TextFieldMui
            id="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            onKeyPress={handleKeyPress}
            label={translations("login:email")}
            error={formik.touched.username && formik.errors.username}
            helperText={
              formik.touched.username &&
              formik.errors.username && <span>{formik.errors.username}</span>
            }
          />
        </div>
        <div className={styles.inputWrapper}>
          <TextFieldMui
            id="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            type={InputPasswordType}
            onKeyPress={handleKeyPress}
            label={translations("login:password")}
            right={ShowVisibilityIcon()}
            error={formik.touched.password && formik.errors.password}
            helperText={
              formik.touched.password &&
              formik.errors.password && <span>{formik.errors.password}</span>
            }
          />
        </div>
      </div>

      {isEcuadorCompany && (
        <SimpleCaptcha
          label={translations("login:description_simple_captcha")}
          handleCaptcha={handleCaptcha}
        />
      )}

      <div className={styles.buttonsWrapper}>
        <ButtonWithVariant
          variant={ButtonVariant.FOURTH}
          className={styles.buttonSignIn}
          onClick={formik.handleSubmit}
          isLoading={signInStore?.fetching}
          disabled={BUTTON_DISABLED}
          label={translations("login:signin")}
        />
        <div className={styles.separator} />

        {Array.isArray(IDPLogin) &&
          IDPLogin.map((provider, index) => (
            <ButtonWithVariant
              key={index}
              variant={ButtonVariant.FOURTH}
              className={styles.buttonSignIn}
              onClick={() => {
                handleOpenIdLogin(provider.idp);
              }}
              label={
                translations("login:signin_openId") +
                provider.providerDisplayName
              }
              isLoading={loginStore?.fetchingPost}
            />
          ))}

        {!googleButtonLoaded && (
          <ButtonWithVariant
            variant={ButtonVariant.FOURTH}
            className={styles.buttonSignIn}
            onClick={handleGoogleLogin}
            icon={googleSVG}
            label={translations("login:signin_google")}
            isLoading={loginStore?.fetchingPost}
          />
        )}

        {googleButtonLoaded && (
          <div className={styles.loginGoogleButton}>
            <div
              id="g_id_onload"
              data-client_id={googleClientId}
              data-context="signin"
              data-ux_mode="popup"
              data-login_uri={`${api}/v2/login/google/oauth?state=${uuid}&status=${STATUS_DONE}`}
              data-auto_select="true"
              data-itp_support="true"
            ></div>

            <div
              className="g_id_signin"
              data-type="standard"
              data-shape="rectangular"
              data-theme="filled_blue"
              data-text="signin_with"
              data-size="large"
              data-width="300"
              data-logo_alignment="left"
            ></div>
          </div>
        )}
      </div>

      <div className={styles.poweredWrapper}>
        <span className={styles.textWrapper}>
          {translations("login:powered")}
        </span>
        <span className={styles.textBoldWrapper}>
          {translations("login:denwa")}
        </span>
      </div>
    </form>
  );
};
export default memo(FormLogin);
